"use client";

import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";

// 1️⃣ Context types
interface ModalContextType {
  openModal: (content: ReactNode) => void;
  closeModal: () => void;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
}

interface ConfirmOptions {
  title: string;
  content: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info";
}

interface PromptOptions {
  title: string;
  input?: { type: string; placeholder?: string; defaultValue?: string };
  confirmText?: string;
  cancelText?: string;
}

// 2️⃣ Context
const ModalContext = createContext<ModalContextType | undefined>(undefined);

// 3️⃣ ModalProvider
interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);

  const openModal = (content: ReactNode) => {
    setModalContent(content);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setTimeout(() => setModalContent(null), 300);
  };

  const confirm = (options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      const handleConfirm = () => {
        resolve(true);
        closeModal();
      };
      const handleCancel = () => {
        resolve(false);
        closeModal();
      };

      const variantColors = {
        danger: "bg-red-500 hover:bg-red-600",
        warning: "bg-amber-500 hover:bg-amber-600",
        info: "bg-blue-500 hover:bg-blue-600",
      };

      const variantIcon = {
        danger: "⚠️",
        warning: "⚠️",
        info: "ℹ️",
      };

      const confirmVariant = options.variant || "danger";
      const confirmButtonClass = variantColors[confirmVariant];
      const confirmIcon = variantIcon[confirmVariant];

      openModal(
        <Modal onClose={handleCancel}>
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
            {/* Header */}
            <div className="relative border-b border-gray-100 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${confirmVariant === "danger" ? "bg-red-100 text-red-600" : confirmVariant === "warning" ? "bg-amber-100 text-amber-600" : "bg-blue-100 text-blue-600"}`}>
                  <span className="text-lg">{confirmIcon}</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {options.title}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Please confirm your action</p>
                </div>
              </div>
              <button
                onClick={handleCancel}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-gray-700 leading-relaxed">
                  {options.content}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
              <button
                onClick={handleCancel}
                className="rounded-lg px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors border border-gray-300 hover:border-gray-400"
              >
                {options.cancelText || "Cancel"}
              </button>
              <button
                onClick={handleConfirm}
                className={`rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-all ${confirmButtonClass} transform hover:scale-[1.02] active:scale-[0.98]`}
              >
                {options.confirmText || "Confirm"}
              </button>
            </div>
          </div>
        </Modal>
      );
    });
  };

  const prompt = (options: PromptOptions): Promise<string | null> => {
    return new Promise<string | null>((resolve) => {
      const PromptContent: React.FC = () => {
        const [inputValue, setInputValue] = useState(options.input?.defaultValue || "");

        const handleConfirm = () => {
          resolve(inputValue.trim());
          closeModal();
        };

        const handleCancel = () => {
          resolve(null);
          closeModal();
        };

        const handleKeyPress = (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' && inputValue.trim()) {
            handleConfirm();
          }
          if (e.key === 'Escape') {
            handleCancel();
          }
        };

        useEffect(() => {
          const input = document.querySelector('input');
          input?.focus();
        }, []);

        return (
          <Modal onClose={handleCancel}>
            <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
              {/* Header */}
              <div className="border-b border-gray-100 px-6 pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {options.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Please provide your input</p>
                  </div>
                  <button
                    onClick={handleCancel}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                    aria-label="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-5">
                <div className="space-y-4">
                  <div className="flex items-center justify-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Input
                    </label>
                    <input
                      type={options.input?.type || "text"}
                      placeholder={options.input?.placeholder || "Type here..."}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyPress}
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
                <button
                  onClick={handleCancel}
                  className="rounded-lg px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors border border-gray-300 hover:border-gray-400"
                >
                  {options.cancelText || "Cancel"}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={!inputValue.trim()}
                  className="rounded-lg px-5 py-2.5 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {options.confirmText || "Submit"}
                </button>
              </div>
            </div>
          </Modal>
        );
      };

      openModal(<PromptContent />);
    });
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal, confirm, prompt }}>
      {children}
      {isOpen && modalContent}
    </ModalContext.Provider>
  );
};

// 4️⃣ Custom hook
export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);
  if (!context) throw new Error("useModal must be used within a ModalProvider");
  return context;
};

// 5️⃣ Enhanced Modal Component
const Modal: React.FC<{ children: ReactNode; onClose: () => void }> = ({
  children,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    document.body.style.overflow = "hidden";
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-10 flex items-center justify-center transition-all duration-300 ${isVisible ? 'bg-black/50 backdrop-blur-sm' : 'bg-transparent'}`}
      onClick={handleBackdropClick}
    >
      <div 
        className={`transform transition-all duration-300 ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};